import { useEffect, useRef } from 'react';
import { createAnalyserFromElement } from '@/utils/sharedAudioContext';

export default function VUMeter({ audioElement, label = 'DECK', color = '#22d3ee', height = 200 }) {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const analyserRef = useRef(null);

  useEffect(() => {
    if (!audioElement || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = 80;
    canvas.height = height;

    const setupAnalyser = () => {
      if (analyserRef.current) return;
      const result = createAnalyserFromElement(audioElement, 256);
      if (result) {
        analyserRef.current = result.analyser;
      }
    };

    const handlePlay = () => setupAnalyser();
    audioElement.addEventListener('play', handlePlay, { once: true });

    const draw = () => {
      if (!ctx) { animationRef.current = requestAnimationFrame(draw); return; }

      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      let level = 0;
      let peak = 0;

      if (analyserRef.current && !audioElement.paused) {
        const bufferLength = analyserRef.current.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        analyserRef.current.getByteFrequencyData(dataArray);
        let sum = 0, max = 0;
        for (let i = 0; i < bufferLength; i++) {
          sum += dataArray[i];
          if (dataArray[i] > max) max = dataArray[i];
        }
        level = sum / bufferLength / 255;
        peak = max / 255;
      }

      const barHeight = canvas.height - 40;
      const levelHeight = barHeight * level;
      const peakHeight = barHeight * peak;

      ctx.strokeStyle = '#333333';
      ctx.lineWidth = 1;
      for (let i = 0; i < 10; i++) {
        const y = 30 + (barHeight / 10) * i;
        ctx.beginPath();
        ctx.moveTo(10, y);
        ctx.lineTo(canvas.width - 10, y);
        ctx.stroke();
      }

      const gradient = ctx.createLinearGradient(0, canvas.height - 10, 0, 30);
      gradient.addColorStop(0, '#22c55e');
      gradient.addColorStop(0.5, '#eab308');
      gradient.addColorStop(0.8, '#f97316');
      gradient.addColorStop(1, '#ef4444');

      ctx.fillStyle = gradient;
      ctx.fillRect(15, canvas.height - 10 - levelHeight, canvas.width - 30, levelHeight);

      if (peak > 0.01) {
        ctx.fillStyle = peak > 0.9 ? '#ef4444' : peak > 0.7 ? '#f97316' : '#eab308';
        ctx.fillRect(10, canvas.height - 10 - peakHeight - 2, canvas.width - 20, 4);
      }

      ctx.fillStyle = color;
      ctx.font = 'bold 10px monospace';
      ctx.textAlign = 'center';
      ctx.fillText(label, canvas.width / 2, 15);

      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 12px monospace';
      ctx.fillText(`${Math.round(level * 100)}%`, canvas.width / 2, canvas.height - 15);

      if (peak > 0.95) {
        ctx.fillStyle = '#ef4444';
        ctx.font = 'bold 9px monospace';
        ctx.fillText('PEAK!', canvas.width / 2, canvas.height - 2);
      }

      animationRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      audioElement.removeEventListener('play', handlePlay);
    };
  }, [audioElement, label, color, height]);

  return (
    <canvas
      ref={canvasRef}
      className="rounded"
      style={{ width: '80px', height: `${height}px`, backgroundColor: '#000' }}
    />
  );
}
